import React, { useState, useMemo } from 'react';
import { CALCULATOR_CONFIG, calculateEstimate } from '../data/calculatorData';
import { WebForm } from './WebForm';

export function PriceCalculator() {
  const [systemTypeId, setSystemTypeId] = useState(CALCULATOR_CONFIG.systemTypes[0].id);
  const [houseSizeId, setHouseSizeId] = useState(CALCULATOR_CONFIG.houseSizes[1].id);
  const [currentHeatId, setCurrentHeatId] = useState(CALCULATOR_CONFIG.currentHeatSources[0].id);
  const [insulationId, setInsulationId] = useState(CALCULATOR_CONFIG.insulationLevels[0].id);
  const [selectedAddOns, setSelectedAddOns] = useState(['demontering', 'varmtvandsbeholder']);
  const [showQuotationForm, setShowQuotationForm] = useState(false);

  const toggleAddOn = (id) => {
    setSelectedAddOns(prev => 
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  const estimate = useMemo(() => {
    return calculateEstimate({
      systemTypeId,
      houseSizeId,
      currentHeatId,
      insulationId,
      selectedAddOns
    });
  }, [systemTypeId, houseSizeId, currentHeatId, insulationId, selectedAddOns]);

  const resetForm = () => {
    setSystemTypeId(CALCULATOR_CONFIG.systemTypes[0].id);
    setHouseSizeId(CALCULATOR_CONFIG.houseSizes[1].id);
    setCurrentHeatId(CALCULATOR_CONFIG.currentHeatSources[0].id);
    setInsulationId(CALCULATOR_CONFIG.insulationLevels[0].id);
    setSelectedAddOns(['demontering', 'varmtvandsbeholder']);
    setShowQuotationForm(false);
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-gray-200/80 overflow-hidden">
      {/* Header Banner */}
      <div className="bg-[#0B0E14] text-white p-6 sm:p-8">
        <div className="max-w-3xl">
          <span className="text-[#206FEA] font-bold text-xs uppercase tracking-widest bg-white/10 px-3 py-1 rounded-full border border-white/10 mb-3 inline-block">
            Uforpligtende Prisberegning
          </span>
          <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
            Vejledende Prisberegner
          </h2>
          <p className="text-sm text-white/80 mt-2 leading-relaxed">
            Beregn et hurtigt og realistisk prisoverslag på din nye energiløsning. Beregningen er 100% uforpligtende og gratis.
          </p>
        </div>
      </div>

      <div className="p-6 sm:p-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Left Form Controls */}
        <div className="lg:col-span-7 space-y-8">
          
          {/* Step 1: System Type */}
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-[#2a2a2a] mb-3 flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-[#2a2a2a] text-white flex items-center justify-center text-xs">1</span>
              Vælg Anlægstype
            </label>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {CALCULATOR_CONFIG.systemTypes.map(st => {
                const selected = st.id === systemTypeId;
                return (
                  <button
                    key={st.id}
                    type="button"
                    onClick={() => setSystemTypeId(st.id)}
                    className={`p-4 rounded-xl text-left border-2 transition-all flex flex-col justify-between ${
                      selected 
                        ? 'border-[#206FEA] bg-[#206FEA]/5 shadow-sm' 
                        : 'border-[#CBD5E1] hover:border-[#94A3B8] bg-white'
                    }`}
                  >
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-bold text-sm text-[#2a2a2a]">{st.name}</span>
                        {selected && <i className="fa-solid fa-circle-check text-[#206FEA]" aria-hidden="true"></i>}
                      </div>
                      <p className="text-xs text-[#4e5d59] leading-snug">{st.description}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Step 2: House Size */}
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-[#2a2a2a] mb-3 flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-[#2a2a2a] text-white flex items-center justify-center text-xs">2</span>
              Boligens / Bygningens Størrelse
            </label>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
              {CALCULATOR_CONFIG.houseSizes.map(size => {
                const selected = size.id === houseSizeId;
                return (
                  <button
                    key={size.id}
                    type="button"
                    onClick={() => setHouseSizeId(size.id)}
                    className={`py-3 px-3 rounded-xl text-center border-2 text-xs font-semibold transition-all ${
                      selected 
                        ? 'border-[#206FEA] bg-[#206FEA] text-white' 
                        : 'border-[#CBD5E1] text-[#0B0E14] hover:bg-[#E6EBEF]'
                    }`}
                  >
                    {size.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Step 3: Current Heat Source */}
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-[#2a2a2a] mb-3 flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-[#2a2a2a] text-white flex items-center justify-center text-xs">3</span>
              Nuværende Opvarmningsform
            </label>

            <select
              value={currentHeatId}
              onChange={(e) => setCurrentHeatId(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#2a2a2a] focus:ring-2 focus:ring-[#2a2a2a]/20 outline-none text-sm font-medium bg-gray-50/50"
            >
              {CALCULATOR_CONFIG.currentHeatSources.map(heat => (
                <option key={heat.id} value={heat.id}>
                  {heat.label}
                </option>
              ))}
            </select>
          </div>

          {/* Step 4: Insulation */}
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-[#2a2a2a] mb-3 flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-[#2a2a2a] text-white flex items-center justify-center text-xs">4</span>
              Isoleringsgrad & Bygningsalder
            </label>

            <select
              value={insulationId}
              onChange={(e) => setInsulationId(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#2a2a2a] focus:ring-2 focus:ring-[#2a2a2a]/20 outline-none text-sm font-medium bg-gray-50/50"
            >
              {CALCULATOR_CONFIG.insulationLevels.map(ins => (
                <option key={ins.id} value={ins.id}>
                  {ins.label}
                </option>
              ))}
            </select>
          </div>

          {/* Step 5: Optional Add-ons */}
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-[#2a2a2a] mb-3 flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-[#2a2a2a] text-white flex items-center justify-center text-xs">5</span>
              Tilvalg & Ekstraudstyr
            </label>

            <div className="space-y-2.5">
              {CALCULATOR_CONFIG.addOns.map(addon => {
                const checked = selectedAddOns.includes(addon.id);
                return (
                  <label
                    key={addon.id}
                    className={`flex items-center justify-between p-3.5 rounded-xl border-2 transition-all cursor-pointer ${
                      checked 
                        ? 'border-[#2a2a2a] bg-[#2a2a2a]/5' 
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleAddOn(addon.id)}
                        className="w-4 h-4 text-[#2a2a2a] rounded border-gray-300 focus:ring-[#2a2a2a]"
                      />
                      <span className="text-sm font-medium text-[#1c2826]">{addon.label}</span>
                    </div>
                    <span className="text-xs font-bold text-[#2a2a2a]">
                      +{addon.price.toLocaleString('da-DK')} kr.
                    </span>
                  </label>
                );
              })}
            </div>
          </div>

          <div className="flex justify-end pt-2">
            <button
              type="button"
              onClick={resetForm}
              className="rounded-xl text-xs font-semibold text-gray-500 hover:text-[#2a2a2a] flex items-center gap-1.5"
            >
              <i className="fa-solid fa-rotate-left" aria-hidden="true"></i>
              <span>Nulstil beregner</span>
            </button>
          </div>

        </div>

        {/* Right Calculation Result Card */}
        <div className="lg:col-span-5 flex flex-col justify-between bg-[#f8f7f2] p-6 sm:p-8 rounded-2xl border border-gray-200">
          <div>
            <div className="flex items-center justify-between pb-4 border-b border-gray-200 mb-6">
              <h3 className="text-lg font-bold text-[#2a2a2a]">
                Beregnet Prisoverslag
              </h3>
              <span className="text-xs font-bold uppercase tracking-wider text-[#d97706] bg-[#d97706]/10 px-2.5 py-1 rounded-md">
                Inkl. Moms
              </span>
            </div>

            <div className="space-y-3 text-sm mb-6">
              <div className="flex justify-between text-[#4e5d59]">
                <span>Anlæg & Grundinstallation:</span>
                <span className="font-semibold text-[#1c2826]">{estimate.base.toLocaleString('da-DK')} kr.</span>
              </div>

              {estimate.heatFee > 0 && (
                <div className="flex justify-between text-[#4e5d59]">
                  <span>Fjernelse af nuværende anlæg:</span>
                  <span className="font-semibold text-[#1c2826]">+{estimate.heatFee.toLocaleString('da-DK')} kr.</span>
                </div>
              )}

              {estimate.insulationFee > 0 && (
                <div className="flex justify-between text-[#4e5d59]">
                  <span>Tilpasning for bygning:</span>
                  <span className="font-semibold text-[#1c2826]">+{estimate.insulationFee.toLocaleString('da-DK')} kr.</span>
                </div>
              )}

              {estimate.addOnsTotal > 0 && (
                <div className="flex justify-between text-[#4e5d59]">
                  <span>Valgte tilvalg ({selectedAddOns.length}):</span>
                  <span className="font-semibold text-[#1c2826]">+{estimate.addOnsTotal.toLocaleString('da-DK')} kr.</span>
                </div>
              )}

              <div className="pt-3 border-t border-gray-200/80 flex justify-between text-xs text-gray-500">
                <span>Heraf moms (25%):</span>
                <span>{estimate.vatAmount.toLocaleString('da-DK')} kr.</span>
              </div>
            </div>

            {/* Total Highlight */}
            <div className="bg-[#2a2a2a] text-white p-6 rounded-xl mb-6 shadow-md">
              <p className="text-xs uppercase tracking-widest text-[#d97706] font-bold mb-1">
                Estimeret Totalpris
              </p>
              <div className="text-3xl sm:text-4xl font-extrabold tracking-tight">
                {estimate.totalInclVat.toLocaleString('da-DK')} <span className="text-lg font-normal text-white/80">kr.</span>
              </div>
              <p className="text-[11px] text-white/70 mt-2">
                Vejledende pris inkl. levering, montering og moms.
              </p>
            </div>

            <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-900 leading-relaxed mb-6">
              <i className="fa-solid fa-circle-info mr-1.5 text-[#d97706]" aria-hidden="true"></i>
              Prisen er et vejledende overslag. Det endelige og bindende tilbud fremsendes efter en kort besigtigelse eller dialog.
            </div>
          </div>

          <div>
            {!showQuotationForm ? (
              <button
                type="button"
                onClick={() => setShowQuotationForm(true)}
                className="w-full bg-[#006dde] hover:bg-[#0058b4] text-white font-bold text-base py-4 rounded-xl shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2 group"
              >
                <span>Få uforpligtende tilbud på din beregning</span>
                <i className="fa-solid fa-arrow-right text-xs group-hover:translate-x-1 transition-transform" aria-hidden="true"></i>
              </button>
            ) : (
              <div className="mt-4 animate-in fade-in duration-200">
                <WebForm 
                  title="Bestil uforpligtende tilbud på din beregning"
                  subtitle={`Vi har vedlagt dit estimat på ca. ${estimate.totalInclVat.toLocaleString('da-DK')} kr. til forespørgslen.`}
                />
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}
